import { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import { useActions } from '../../../hooks/useActions';
import { useProcessing } from '../../../hooks/useProcessing';
import { IMemberListItem } from '../../../models/MemberListItem';
import { IPerson } from '../../../models/Person';
import { RoutePath } from '../../../models/enums/RoutePath';
import { updateModalState } from '../../../store/redux-slices/modals';
import { MEMBERS_LOADING_PROCESSING } from '../../../store/redux-slices/processes/constants';
import { EDIT_MEMBER_MODAL_NAME } from '../../../styledComponents/Modals/EditMemberModal/editMemberModal.const';
import { textContainsWords } from '../../../utils/textHelpers';
import { MemberOverviewPageParamsType } from '../../../models/types/RoutePath';
import { useNavigateSearch } from '../../../hooks/useNavigateSearch';
import { loadSRMembers } from '../../../store/saga-slices/membership/securityResponse';
import {
  clearSRMembers,
  updateSRMembersList,
} from '../../../store/redux-slices/membership/securityResponse';
import { pageListSRMembersSelector } from '../../../store/redux-slices/membership/securityResponse/selectors';
import { srMembershipPlansPricesSelector } from '../../../store/redux-slices/pricing/securityResponse/selectors';
import { srLoadStandardPricing } from '../../../store/saga-slices/pricing/securityResponse';
import { clearSRMembershipPlansPrices } from '../../../store/redux-slices/pricing/securityResponse';
import { ILoadSRMembersPayload } from '../../../store/saga-slices/membership/securityResponse/models';
import { RequiredQueryParams } from './membersSecurityResponse.models';

export const useMembersSecurityResponse = () => {
  const LoadMembers = useActions(loadSRMembers);
  const ClearMembers = useActions(clearSRMembers);
  const location = useLocation();
  const navigate = useNavigateSearch();
  const pageListMembers = useSelector(pageListSRMembersSelector);
  const srMembershipPricing = useSelector(srMembershipPlansPricesSelector);

  const membersLoading = useProcessing(MEMBERS_LOADING_PROCESSING);

  const LoadStandardPricing = useActions(srLoadStandardPricing);
  const ClearStandardPricing = useActions(clearSRMembershipPlansPrices);

  const UpdateModalState = useActions(updateModalState);
  const UpdateMembersList = useActions(updateSRMembersList);

  useEffect(() => {
    LoadStandardPricing();
  }, [LoadStandardPricing]);

  const loadMembersHandler = useCallback(() => {
    const params = location.search.slice(1);

    if (!textContainsWords(params, RequiredQueryParams)) {
      return;
    }

    const payload: ILoadSRMembersPayload = {
      params,
    };

    LoadMembers(payload);
  }, [LoadMembers, location.search]);

  useEffect(() => loadMembersHandler(), [loadMembersHandler]);

  useEffect(
    () => () => {
      ClearMembers();
      ClearStandardPricing();
    },
    [ClearMembers, ClearStandardPricing],
  );
  useEffect(() => () => ClearMembers(), [ClearMembers]);

  const onRowClickHandler = useCallback(
    (row: IMemberListItem) => {
      const params: MemberOverviewPageParamsType = {
        memberId: row.personId,
      };
      navigate(RoutePath.MemberOverviewPage, { params });
    },
    [navigate],
  );

  const rowEditHandler = useCallback(
    (id: string) => {
      UpdateModalState([EDIT_MEMBER_MODAL_NAME, id]);
    },
    [UpdateModalState],
  );

  const onMemberEditedHandler = useCallback(
    (person: IPerson) => {
      UpdateMembersList(person);
    },
    [UpdateMembersList],
  );

  return {
    membersLoading,
    pageListMembers,
    onRowClickHandler,
    rowEditHandler,
    onMemberEditedHandler,
    srMembershipPricing,
  };
};
